import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Register.css";

import elephant from "../assets/login/elephant.png";
import tuktuk from "../assets/login/tuktuk.png";
import lion from "../assets/login/lion.png";
import turtle from "../assets/login/turtle.png";
import palmtree from "../assets/login/palmtree.png";
import monkey from "../assets/login/monkey.png";
import flag from "../assets/login/flag.png";
import mango from "../assets/login/mango.png";
import crab from "../assets/login/crab.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const pictureChoices = [
  { name: "Elephant", src: elephant },
  { name: "Tuk Tuk", src: tuktuk },
  { name: "Lion", src: lion },
  { name: "Turtle", src: turtle },
  { name: "Palm Tree", src: palmtree },
  { name: "Monkey", src: monkey },
  { name: "Flag", src: flag },
  { name: "Mango", src: mango },
  { name: "Crab", src: crab },
];

export default function Register() {
  const goToPage = useNavigate();

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [selectedPictures, setSelectedPictures] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedCards = useMemo(() => {
    return selectedPictures
      .map((selectedName) =>
        pictureChoices.find((picture) => picture.name === selectedName)
      )
      .filter(Boolean);
  }, [selectedPictures]);

  function handleSelect(picture) {
    const selectedIndex = selectedPictures.indexOf(picture.name);

    // clicking a picture again removes it, which helps if the order is wrong
    if (selectedIndex !== -1) {
      setSelectedPictures(
        selectedPictures.filter((item) => item !== picture.name)
      );
      return;
    }

    // the pattern is limited to three pictures because login checks the same length
    if (selectedPictures.length < 3) {
      setSelectedPictures([...selectedPictures, picture.name]);
    }
  }

  function handleOpenConfirm() {
    if (selectedPictures.length !== 3) {
      alert("Pick 3 pictures first!");
      return;
    }

    setShowConfirmModal(true);
  }

  async function handleFinalCreateAccount() {
    const trimmedName = name.trim();

    if (!trimmedName || selectedPictures.length !== 3) {
      alert("Please enter your name and pick 3 pictures.");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/api/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: trimmedName,
          secret_images: selectedPictures,
        }),
      });

      const resultData = await response.json();

      if (!response.ok) {
        alert(resultData.error || "Could not create account.");
        setIsSubmitting(false);
        return;
      }

      setShowConfirmModal(false);
      alert("Account created!");
      goToPage("/loading", { state: { to: "/login" } });
    } catch (error) {
      console.error("Register error:", error);
      alert("Server error. Make sure your Flask app is running.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="registerPage">
      <button
        className="backBtn"
        onClick={() => goToPage("/loading", { state: { to: "/" } })}
        type="button"
      >
        ← Back
      </button>

      {step === 1 && (
        <div className="stepContainer">
          <h1 className="registerTitle">What&apos;s Your Name?</h1>

          <input
            className="nameInput fun"
            placeholder="Type your name here..."
            value={name}
            onChange={(event) => setName(event.target.value)}
          />

          <button
            className="nextBtn"
            onClick={() => {
              if (!name.trim()) {
                alert("Enter your name first!");
                return;
              }

              setStep(2);
            }}
            type="button"
          >
            Next →
          </button>
        </div>
      )}

      {step === 2 && (
        <>
          <h1 className="registerTitle">
            Hi {name}! Pick Your Secret Pattern
          </h1>

          <p className="instruction">Choose 3 pictures in order</p>

          <div className="selectedPreview">
            {selectedPictures.length === 0 ? (
              <span className="selectedPlaceholder">
                No pictures chosen yet
              </span>
            ) : (
              selectedPictures.map((item, index) => (
                <span key={item} className="selectedItem">
                  {index + 1}. {item}
                </span>
              ))
            )}
          </div>

          <div className="imageGrid">
            {pictureChoices.map((picture) => {
              const selectedIndex = selectedPictures.indexOf(picture.name);
              const isSelected = selectedIndex !== -1;

              return (
                <button
                  key={picture.name}
                  className={`imageCard ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(picture)}
                  type="button"
                >
                  {isSelected && (
                    <div className="numberBadge">{selectedIndex + 1}</div>
                  )}

                  <img src={picture.src} alt={picture.name} />
                  <p>{picture.name}</p>
                </button>
              );
            })}
          </div>

          <button
            className="submitBtn"
            onClick={handleOpenConfirm}
            type="button"
          >
            Create Account
          </button>
        </>
      )}

      {showConfirmModal && (
        <div
          className="modalOverlay"
          onClick={() => {
            if (!isSubmitting) {
              setShowConfirmModal(false);
            }
          }}
        >
          <div
            className="confirmModal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="modalTitle">Are You Sure?</h2>

            <p className="modalText">
              This will be your secret picture pattern.
            </p>

            <p className="modalReminder">
              Make sure you remember this order:
            </p>

            <div className="modalSequence">
              {selectedCards.map((card, index) => (
                <div key={card.name} className="modalCard">
                  <div className="modalBadge">{index + 1}</div>
                  <img src={card.src} alt={card.name} />
                  <p>{card.name}</p>
                </div>
              ))}
            </div>

            <div className="modalButtons">
              <button
                className="modalBtn modalBtnSecondary"
                onClick={() => setShowConfirmModal(false)}
                type="button"
                disabled={isSubmitting}
              >
                Go Back
              </button>

              <button
                className="modalBtn modalBtnPrimary"
                onClick={handleFinalCreateAccount}
                type="button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Creating..." : "Yes, Create My Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}